// FormBuilderOthersSection — the "Others" body inside Advanced options, per
// the design: Field ID first (auto-generated note + locked read-only pill —
// it identifies the field in entries, never editable), then Default value and
// Short title labelled Inputs, and an "Is this personal data?" Checkbox.

import { useTranslation } from 'react-i18next';
import { Checkbox, Icon, Input } from '@/components/common';
import type { ElementProperties } from './utils/formElementDefaults';
import type { BuilderField, BuilderPage } from './utils/builderModel';

interface FormBuilderOthersSectionProps {
  field: BuilderField | BuilderPage;
  onChange: (properties: ElementProperties) => void;
}

const FormBuilderOthersSection = ({ field, onChange }: FormBuilderOthersSectionProps) => {
  const { t } = useTranslation();

  const properties = field.properties;
  if (!('otherProperties' in properties)) return null;
  const op = properties.otherProperties as Record<string, unknown>;

  const setOthers = (patch: Record<string, unknown>) =>
    onChange({
      ...properties,
      otherProperties: { ...(properties.otherProperties as object), ...patch },
    } as ElementProperties);

  const textRow = (key: 'defaultValue' | 'shortTitle') => (
    <div className='form-builder-props__lay-row'>
      <span className='form-builder-props__label'>{t(`forms_builder_props_oth_${key}`)}</span>
      <Input
        size='sm'
        value={(op[key] as string) ?? ''}
        placeholder={t(`forms_builder_props_oth_${key}_ph`)}
        aria-label={t(`forms_builder_props_oth_${key}`)}
        onChange={(e) => setOthers({ [key]: e.target.value })}
      />
    </div>
  );

  return (
    <div className='form-builder-props__oth'>
      <div className='form-builder-props__lay-row'>
        <span className='form-builder-props__label form-builder-props__oth-id-label'>
          {t('forms_builder_props_oth_fieldUniqueId')}
          <span className='form-builder-props__label-note'>
            {t('forms_builder_props_oth_auto')}
          </span>
        </span>
        <span className='form-builder-props__oth-id'>
          <Icon name='lock' size={14} />
          {(op.fieldUniqueId as string) || '—'}
        </span>
      </div>

      {textRow('defaultValue')}
      {textRow('shortTitle')}

      <Checkbox
        label={t('forms_builder_props_oth_isPersonalData')}
        checked={op.isPersonalData === true}
        onChange={(e) => setOthers({ isPersonalData: e.target.checked })}
        size='sm'
      />
    </div>
  );
};

export default FormBuilderOthersSection;
